/**
 * WebSocket hook for live event updates via Socket.IO
 * 
 * NOTE: Auto-connect disabled for dashboard. Dashboard uses JWT authentication
 * but WebSocket requires API keys (for agent subscribers). Dashboard displays
 * historical data via REST APIs and doesn't need real-time WebSocket updates.
 * 
 * Socket.IO client is lazy-loaded only when explicitly calling connect(),
 * preventing unnecessary bundle loading and deprecation warnings.
 * 
 * To enable live updates in the future, implement JWT-based WebSocket auth
 * in the backend ws-adapter.
 */
import { useRef, useState, useCallback } from 'react';
import type { Socket } from 'socket.io-client';
import { getAuthToken } from './api';

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';

interface WebhookEventPayload {
  event: string;
  action?: string;
  delivery_id: string;
  repository?: string;
  sender?: string;
  summary: string;
  payload: any;
  received_at: string;
  dispatched_at: string;
}

export function useWebSocket() {
  const socketRef = useRef<Socket | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [liveEvents, setLiveEvents] = useState<WebhookEventPayload[]>([]);
  const listenersRef = useRef<Set<(event: WebhookEventPayload) => void>>(new Set());

  const connect = useCallback(async () => {
    const token = getAuthToken();
    if (!token || socketRef.current?.connected) {
      console.log('[WebSocket] Not connecting: No token or already connected');
      return;
    }

    // Lazy load Socket.IO client to avoid deprecation warnings when not in use
    const { io } = await import('socket.io-client');

    const socket = io(window.location.origin, {
      path: '/ws',
      auth: { apiKey: token, name: 'dashboard-spa' },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    socket.on('connect', () => setStatus('connected'));
    socket.on('disconnect', () => setStatus('disconnected'));
    socket.on('connect_error', () => setStatus('disconnected'));

    socket.on('connected', () => {
      setStatus('connected');
      // Subscribe to all events
      socket.emit('subscribe', { events: ['*'], repos: ['*'] });
    });

    socket.on('webhook-event', (event: WebhookEventPayload) => {
      setLiveEvents(prev => [event, ...prev].slice(0, 100));
      listenersRef.current.forEach(cb => cb(event));
    });

    socketRef.current = socket;
    setStatus('connecting');
  }, []);

  const disconnect = useCallback(() => {
    socketRef.current?.disconnect();
    socketRef.current = null;
    setStatus('disconnected');
  }, []);

  const onEvent = useCallback((cb: (event: WebhookEventPayload) => void) => {
    listenersRef.current.add(cb);
    return () => { listenersRef.current.delete(cb); };
  }, []);

  // NOTE: Auto-connect disabled - dashboard doesn't need WebSocket
  // useEffect(() => {
  //   connect();
  //   return () => { disconnect(); };
  // }, [connect, disconnect]);

  return { status, liveEvents, onEvent, connect, disconnect };
}
